import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { colors as theme } from '../theme';
import { claimPromptSlot, hasCompletedAWorkout, WORKOUT_COMPLETED_EVENT } from '../utils/promptOncePerVisit';

const DISMISSED_KEY = 'cipher_push_dismissed_v1';

const PushNotificationPrompt = () => {
  const { isSupported, permission, isSubscribed, subscribe } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isSupported) return;
    if (permission === 'granted') return;
    if (permission === 'denied') return;
    if (isSubscribed) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // Asked once the client has finished a workout (14 October 2026): right after the first one,
    // or on a later visit, and never on the same visit as the add-to-home-screen guide.
    let t;
    const offer = (delay) => {
      clearTimeout(t);
      t = setTimeout(() => { if (claimPromptSlot('notifications')) setVisible(true); }, delay);
    };
    if (hasCompletedAWorkout()) offer(3000);
    const onCompleted = () => offer(1500);
    window.addEventListener(WORKOUT_COMPLETED_EVENT, onCompleted);
    return () => { clearTimeout(t); window.removeEventListener(WORKOUT_COMPLETED_EVENT, onCompleted); };
  }, [isSupported, permission, isSubscribed]);

  if (!visible || done) return null;

  const handleEnable = async () => {
    setLoading(true);
    const success = await subscribe();
    setLoading(false);
    if (success) {
      setDone(true);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: '80px', left: '16px', right: '16px',
      zIndex: 900, maxWidth: '420px', margin: '0 auto',
    }}>
      <div style={{
        backgroundColor: theme.surface1,
        border: '1px solid rgba(212,175,55,0.4)',
        borderRadius: '14px',
        padding: '16px 18px',
        display: 'flex', gap: '12px', alignItems: 'flex-start',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0 }}>🔔</div>
        <div style={{ flex: 1 }}>
          <p style={{ color: theme.text, fontSize: '14px', fontWeight: 600, margin: '0 0 4px' }}>
            Want a reminder before your next session?
          </p>
          <p style={{ color: theme.textMuted, fontSize: '13px', margin: '0 0 12px', lineHeight: 1.5 }}>
            We'll send a nudge on your training days.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleEnable}
              disabled={loading}
              style={{
                flex: 1, padding: '9px', backgroundColor: theme.gold,
                border: 'none', borderRadius: '8px', color: theme.bg,
                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {loading ? 'Turning on…' : 'Yes, remind me'}
            </button>
            <button
              onClick={handleDismiss}
              style={{
                padding: '9px 14px', backgroundColor: 'transparent',
                border: '1px solid rgba(42,53,98,0.8)', borderRadius: '8px',
                color: theme.textMuted, fontSize: '13px', cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
